import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Button,
  Link,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Divider,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';

const navItemSx = {
  fontFamily: 'Outfit',
  fontWeight: 400,
  fontSize: '15px',
  color: '#ffffff',
  textDecoration: 'none',
  cursor: 'pointer',
  letterSpacing: '0.01em',
  transition: 'color 0.2s',
  '&:hover': { color: '#004D40' },
};

const mobileLinkSx = {
  fontFamily: 'Outfit',
  fontWeight: 500,
  fontSize: '17px',
  color: '#ffffff',
  py: 1.5,
};

const TopBarSection = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const specOpen = Boolean(anchorEl);

  const handleSpecOpen = (e) => setAnchorEl(e.currentTarget);
  const handleSpecClose = () => setAnchorEl(null);

  const openMobile = () => setMobileOpen(true);
  const closeMobile = () => {
    setMobileOpen(false);
    setMobileProductsOpen(false);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'rgba(0,0,0,0.95)',
        backdropFilter: 'blur(18px)',
        WebkitBackdropFilter: 'blur(18px)',
        boxShadow: 'none',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        zIndex: 1300,
      }}
    >
      <Toolbar sx={{ height: '64px', px: { xs: 2, md: 5 } }}>
        {/* Logo */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
          <Box
            component={RouterLink}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
          >
            <Box
              component="img"
              src="./companynamelogowhite_32.png"
              alt="Company Logo"
              sx={{ height: 32 }}
            />
          </Box>
        </Box>

        {/* Centre navigation (desktop) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: '36px',
          }}
        >
          <Link component={RouterLink} to="/" underline="none" sx={navItemSx}>
            Home
          </Link>

          {/* Specification dropdown */}
          <Box
            onClick={handleSpecOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: 'pointer',
              color: specOpen ? '#004D40' : '#ffffff',
              transition: 'color 0.2s',
              '&:hover': { color: '#004D40' },
            }}
          >
            <Typography sx={{ fontFamily: 'Outfit', fontWeight: 400, fontSize: '15px' }}>
              Products
            </Typography>
            <KeyboardArrowDownIcon
              sx={{
                fontSize: 18,
                transition: 'transform 0.2s',
                transform: specOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={specOpen}
            onClose={handleSpecClose}
            disableScrollLock
            PaperProps={{
              sx: {
                backgroundColor: '#000000',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: '6px',
                mt: 0.5,
                minWidth: 140,
                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              },
            }}
            transformOrigin={{ horizontal: 'center', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
          >
            <MenuItem
              component={RouterLink}
              to="/darwin"
              onClick={handleSpecClose}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '14px',
                color: '#ffffff',
                py: 1,
                '&:hover': { color: '#004D40', backgroundColor: 'rgba(0,77,64,0.1)' },
              }}
            >
              Darwin
            </MenuItem>
            <MenuItem
              component={RouterLink}
              to="/eden"
              onClick={handleSpecClose}
              sx={{
                fontFamily: 'Outfit',
                fontSize: '14px',
                color: '#ffffff',
                py: 1,
                '&:hover': { color: '#004D40', backgroundColor: 'rgba(0,77,64,0.1)' },
              }}
            >
              Eden
            </MenuItem>
          </Menu>

          <Link component={RouterLink} to="/installation" underline="none" sx={navItemSx}>
            Installation
          </Link>

          <Link component={RouterLink} to="/faqs" underline="none" sx={navItemSx}>
            FAQs
          </Link>
        </Box>

        {/* Right side: login (desktop) + hamburger (mobile) */}
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
          <Button
            href="https://app.sigmatiqa.com"
            disableElevation
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              fontFamily: 'Outfit',
              fontWeight: 600,
              fontSize: '13px',
              color: '#ffffff',
              textTransform: 'none',
              letterSpacing: '0.02em',
              backgroundColor: '#004D40',
              borderRadius: '6px',
              px: 2.5,
              py: 0.85,
              '&:hover': { backgroundColor: '#00695C' },
              transition: 'background-color 0.2s',
            }}
          >
            Log in
          </Button>
          <IconButton
            aria-label="Open navigation menu"
            onClick={openMobile}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: '#ffffff',
            }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Toolbar>

      {/* Mobile drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={closeMobile}
        PaperProps={{
          sx: {
            backgroundColor: '#000000',
            color: '#ffffff',
            width: { xs: '82vw', sm: 320 },
            borderLeft: '1px solid rgba(255,255,255,0.08)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Box
            component="img"
            src="./companynamelogowhite_32.png"
            alt="Company Logo"
            sx={{ height: 28 }}
          />
          <IconButton onClick={closeMobile} aria-label="Close navigation menu" sx={{ color: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <List sx={{ px: 1, py: 1 }}>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/" onClick={closeMobile}>
              <ListItemText primaryTypographyProps={mobileLinkSx} primary="Home" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton onClick={() => setMobileProductsOpen((v) => !v)}>
              <ListItemText primaryTypographyProps={mobileLinkSx} primary="Products" />
              {mobileProductsOpen ? <ExpandLess sx={{ color: '#ffffff' }} /> : <ExpandMore sx={{ color: '#ffffff' }} />}
            </ListItemButton>
          </ListItem>
          <Collapse in={mobileProductsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton component={RouterLink} to="/darwin" onClick={closeMobile} sx={{ pl: 4 }}>
                <ListItemText
                  primaryTypographyProps={{ ...mobileLinkSx, fontSize: '15px', color: 'rgba(255,255,255,0.85)' }}
                  primary="Darwin"
                />
              </ListItemButton>
              <ListItemButton component={RouterLink} to="/eden" onClick={closeMobile} sx={{ pl: 4 }}>
                <ListItemText
                  primaryTypographyProps={{ ...mobileLinkSx, fontSize: '15px', color: 'rgba(255,255,255,0.85)' }}
                  primary="Eden"
                />
              </ListItemButton>
            </List>
          </Collapse>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/installation" onClick={closeMobile}>
              <ListItemText primaryTypographyProps={mobileLinkSx} primary="Installation" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/faqs" onClick={closeMobile}>
              <ListItemText primaryTypographyProps={mobileLinkSx} primary="FAQs" />
            </ListItemButton>
          </ListItem>
        </List>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', mt: 'auto' }} />
        <Box sx={{ p: 2 }}>
          <Button
            fullWidth
            href="https://app.sigmatiqa.com"
            disableElevation
            sx={{
              fontFamily: 'Outfit',
              fontWeight: 600,
              fontSize: '14px',
              color: '#ffffff',
              textTransform: 'none',
              letterSpacing: '0.02em',
              backgroundColor: '#004D40',
              borderRadius: '6px',
              py: 1.1,
              '&:hover': { backgroundColor: '#00695C' },
            }}
          >
            Log in to Dashboard
          </Button>
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default TopBarSection;
